import { queryOptions } from "@tanstack/react-query";
import { config } from "@/config";

// Public (guest) share payload for GET /sr/:token — no auth. Mirrors the shapes
// the backend whitelists (no tenant internals, no note createdByUserId).

export interface ShareReviewComment {
  id: string;
  type: string;
  text: string;
  exactSecond: number;
  sceneNumber: number | null;
  severity: string | null;
  /** Display-only for guests — they see resolved state but cannot change it. */
  resolvedAt: string | null;
}

export interface ShareReviewNote {
  id: string;
  aiCommentId: string | null;
  anchorSecond: number | null;
  authorName: string;
  authorKind?: string;
  body: string;
  resolvedAt: string | null;
  createdAt: string;
}

export interface ScriptReviewSharePayload {
  v: 1;
  id: string;
  status: string;
  /** Version number of the shared cut (1 = original). */
  version: number;
  videoUrl: string | null;
  comments: ShareReviewComment[];
  notes: ShareReviewNote[];
  /** Comment-pass names that failed after retries. Empty when every pass succeeded. */
  failedPasses: string[];
}

export const scriptReviewShareKeys = {
  all: ["script-review-share"] as const,
  detail: (token: string) => [...scriptReviewShareKeys.all, token] as const,
};

async function fetchScriptReviewShare(token: string): Promise<ScriptReviewSharePayload> {
  // Public fetch — no auth/Bearer, no cookies needed.
  const res = await fetch(`${config.apiUrl}/sr/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Review not found (${res.status})`);
  }
  const json = (await res.json()) as { data: ScriptReviewSharePayload };
  return json.data;
}

export function scriptReviewShareQueryOptions(token: string) {
  return queryOptions({
    queryKey: scriptReviewShareKeys.detail(token),
    queryFn: () => fetchScriptReviewShare(token),
    // Collaboration is interactive — always refetch so new comments appear.
    staleTime: 0,
    retry: 1,
    enabled: !!token,
  });
}

/** Post a guest comment/reply (name + body + target). Throws on failure. */
export async function postGuestNote(
  token: string,
  input: { name: string; body: string } & ({ aiCommentId: string } | { anchorSecond: number }),
): Promise<void> {
  const res = await fetch(`${config.apiUrl}/sr/${encodeURIComponent(token)}/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Could not post the comment (${res.status})`);
  }
}
